/**
 * BuildingDetailPanel.jsx
 * Displays detailed information about a selected or hovered building
 */

import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Chip,
  CircularProgress,
  IconButton,
  Divider,
  Alert
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import GroupIcon from '@mui/icons-material/Group';
import SignalCellularAltIcon from '@mui/icons-material/SignalCellularAlt';
import { getStatusByAvailability } from '../utils/statusColorMap';

const BuildingDetailPanel = ({
  building,
  status,
  isLoading = false,
  error = null,
  onClose,
  onUsersClick,
  onAlarmsClick,
  style = {}
}) => {
  if (!building) {
    return null;
  }

  const statusInfo = getStatusByAvailability(status?.availability || building.availability);

  return (
    <Paper
      elevation={8}
      sx={{
        position: 'absolute',
        bottom: 24,
        right: 24,
        width: { xs: '90%', sm: 380 },
        maxHeight: '70vh',
        overflowY: 'auto',
        zIndex: 100,
        backgroundColor: '#fff',
        borderRadius: 2,
        ...style
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 2,
          borderBottom: '1px solid #e0e0e0',
          backgroundColor: statusInfo.color.hex
        }}
      >
        <Typography
          variant="h6"
          sx={{
            color: '#fff',
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}
        >
          <LocationOnIcon sx={{ fontSize: 20 }} />
          {building.name}
        </Typography>
        <IconButton
          size="small"
          onClick={onClose}
          sx={{ color: '#fff' }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Loading State */}
      {isLoading && (
        <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <CircularProgress size={30} />
        </Box>
      )}

      {/* Error State */}
      {error && !isLoading && (
        <Box sx={{ p: 2 }}>
          <Alert severity="error" onClose={() => {}}>{error}</Alert>
        </Box>
      )}

      {/* Content */}
      {!isLoading && !error && (
        <Box sx={{ p: 2 }}>
          {/* Status Section */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ color: '#666', mb: 1 }}>
              STATUS
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Chip
                label={statusInfo.label}
                sx={{
                  backgroundColor: statusInfo.color.hex,
                  color: '#fff',
                  fontWeight: 600,
                  fontSize: '0.95rem'
                }}
              />
              <Typography variant="caption" sx={{ color: '#999' }}>
                {statusInfo.description}
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Metrics Grid */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            {/* Availability */}
            <Grid item xs={6}>
              <Box>
                <Typography variant="caption" sx={{ color: '#999', display: 'block', mb: 0.5 }}>
                  <SignalCellularAltIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
                  Availability
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {(status?.availability || building.availability).toFixed(1)}%
                </Typography>
              </Box>
            </Grid>

            {/* User Count */}
            <Grid item xs={6}>
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: onUsersClick ? '#1565C0' : '#999',
                    display: 'block',
                    mb: 0.5,
                    cursor: onUsersClick ? 'pointer' : 'default',
                    textDecoration: onUsersClick ? 'underline' : 'none',
                    textUnderlineOffset: 2
                  }}
                  onClick={() => {
                    if (onUsersClick) onUsersClick();
                  }}
                >
                  <GroupIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
                  Users
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {status?.userCount ?? building.userCount}
                </Typography>
              </Box>
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          {/* Details */}
          <Box>
            <Typography variant="subtitle2" sx={{ color: '#666', mb: 1 }}>
              INFRASTRUCTURE
            </Typography>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" sx={{ color: '#666' }}>
                Radio Count:
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {building.cellhubCount}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography
                variant="body2"
                sx={{
                  color: onAlarmsClick ? '#1565C0' : '#666',
                  cursor: onAlarmsClick ? 'pointer' : 'default',
                  textDecoration: onAlarmsClick ? 'underline' : 'none',
                  textUnderlineOffset: 2
                }}
                onClick={() => {
                  if (onAlarmsClick) onAlarmsClick();
                }}
              >
                Building Alarms:
              </Typography>
              <Chip
                label={status?.buildingAlarm ?? building.buildingAlarm ?? 0}
                size="small"
                color={(status?.buildingAlarm ?? building.buildingAlarm ?? 0) > 0 ? 'error' : 'default'}
                variant="outlined"
              />
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Location */}
          <Box>
            <Typography variant="caption" sx={{ color: '#999', display: 'block', mb: 0.5 }}>
              COORDINATES
            </Typography>
            <Typography variant="caption" sx={{ color: '#666', display: 'block' }}>
              {building.lat.toFixed(6)}, {building.lng.toFixed(6)}
            </Typography>
          </Box>

          {/* Timestamp */}
          {status?.timestamp && (
            <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #e0e0e0' }}>
              <Typography variant="caption" sx={{ color: '#999' }}>
                Last updated: {new Date(status.timestamp).toLocaleTimeString()}
              </Typography>
            </Box>
          )}
        </Box>
      )}
    </Paper>
  );
};

export default BuildingDetailPanel;
